/**
 * The partner a claim concerns (WPS-163).
 *
 * A claim inherits its registration's partner, so most claims need no
 * action here and the card simply says where the partner came from.
 * Assigning one on the claim sets an override, for the cases that genuinely
 * differ: a second partner called out to a repair, or a claim with no
 * registration to inherit from (a job stop). Clearing the override returns the
 * claim to inheriting rather than leaving it with nobody.
 *
 * Deliberately built from the same three pieces the registration uses - the
 * profile panel, the search, and the inline capture form - so the two surfaces
 * cannot drift into showing a partner differently.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Building2, Handshake, Pencil, Plus, RefreshCw, X } from 'lucide-react'

import { PartnerCreateDialog } from '@/admin/components/shared/partner-create-inline-form'
import { PartnerProfilePanel } from '@/admin/components/shared/partner-profile-panel'
import { DetailCard } from '@/admin/components/shared/detail-card'
import { useExternalRoles } from '@/admin/hooks/use-external-roles'
import { useSetClaimPartner } from '@/admin/hooks/use-manual-partner'
import { useOrg } from '@/admin/hooks/use-org'
import { usePartnershipSearch } from '@/admin/hooks/use-products'
import type { Partnership } from '@/admin/types/organisations'
import { Button } from '@/shared/components/ui/button'
import { SearchInput } from '@/shared/components/ui/search-input'
import { toastApiError } from '@/shared/lib/api-error'

const LABEL_CLASS =
  'text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]'

export function ClaimPartnerCard({
  claimUuid,
  partner,
  isInherited,
  canEdit,
}: {
  claimUuid: string
  /** The effective partner: the claim's override, else the registration's. */
  partner: Partnership | null | undefined
  /** True when the partner shown comes from the registration. */
  isInherited?: boolean
  canEdit: boolean
}) {
  const { t } = useTranslation()
  const { orgUuid } = useOrg()
  const { data: externalRoles = [] } = useExternalRoles()
  const setPartner = useSetClaimPartner(claimUuid)

  const [picking, setPicking] = useState(false)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Partnership | null>(null)
  const [search, setSearch] = useState('')

  // Label the card in the tenant's own word for this partner type
  // (Contractor, Dealer, Garage, ...), taken from the partnership's own role
  // when it has one so a tenant with several roles still reads correctly.
  const partnerRole = externalRoles[0]
  const roleName = partner?.role_name ?? partnerRole?.name ?? 'Partner'

  const { data: results } = usePartnershipSearch(orgUuid, search, {
    includeLocations: true,
    role: partnerRole?.uuid,
  })
  const matches = (results ?? []).filter((p) => p.is_active)

  async function assign(partnershipUuid: string | null) {
    try {
      await setPartner.mutateAsync({ partnershipUuid })
      setPicking(false)
      setCreating(false)
      setEditing(null)
      setSearch('')
    } catch (error) {
      toastApiError(
        error,
        t('claimContractor.errorUpdate', 'Could not update the {{role}}.', {
          role: roleName.toLowerCase(),
        }),
      )
    }
  }

  const busy = setPartner.isPending

  return (
    <DetailCard
      title={roleName}
      icon={<Handshake className="h-3.5 w-3.5" />}
      collapsible={false}
      // WPS-166: this card renders one bespoke block, so the row-count collapse
      // cannot reach it and it ran ~600px - tall enough to set the height of
      // every card beside it in the Overview's aligned grid. Capped to roughly
      // the company identity block, with the rest a click away.
      maxContentHeight={300}
      trailing={
        partner && isInherited ? (
          <span className={LABEL_CLASS}>
            {t('claimContractor.fromRegistration', 'From registration')}
          </span>
        ) : partner ? (
          <span className={LABEL_CLASS}>
            {t('claimContractor.setOnThisClaim', 'Set on this claim')}
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3 px-4 py-3">
        {partner ? (
          <>
            {/* The card body already supplies the padding. */}
            <PartnerProfilePanel partnership={partner} className="p-0" />

            {canEdit && !editing && !picking && (
              <div className="flex flex-wrap items-center gap-3">
                {partner.origin === 'MANUAL' && (
                  <Button
                    variant="ghost"
                    size="xs"
                    disabled={busy}
                    onClick={() => setEditing(partner)}
                    className="h-auto px-0 text-[11px] text-[var(--green)]"
                  >
                    <Pencil className="mr-1 h-3 w-3" />
                    {t('claimContractor.edit', 'Edit {{role}}', {
                      role: roleName.toLowerCase(),
                    })}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="xs"
                  disabled={busy}
                  onClick={() => setPicking(true)}
                  className="h-auto px-0 text-[11px] text-[var(--green)]"
                >
                  <RefreshCw className="mr-1 h-3 w-3" />
                  {t('claimContractor.change', 'Change {{role}}', {
                    role: roleName.toLowerCase(),
                  })}
                </Button>
                {/* Only an override can be cleared. Clearing an inherited
                    partner would mean editing the registration, which does
                    not belong on the claim. */}
                {!isInherited && (
                  <Button
                    variant="ghost"
                    size="xs"
                    disabled={busy}
                    onClick={() => assign(null)}
                    className="h-auto px-0 text-[11px] text-[var(--ink-3)]"
                  >
                    <X className="mr-1 h-3 w-3" />
                    {t(
                      'claimContractor.useRegistration',
                      "Use the registration's {{role}}",
                      { role: roleName.toLowerCase() },
                    )}
                  </Button>
                )}
              </div>
            )}
          </>
        ) : (
          !picking && (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[12px] text-[var(--ink-3)]">
                {t(
                  'claimContractor.none',
                  'No {{role}} on this claim or its registration.',
                  { role: roleName.toLowerCase() },
                )}
              </span>
              {canEdit && (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => setPicking(true)}
                  className="h-auto px-0 text-[11px] text-[var(--green)]"
                >
                  <Plus className="mr-1 h-3 w-3" />
                  {t('claimContractor.assign', 'Assign {{role}}', {
                    role: roleName.toLowerCase(),
                  })}
                </Button>
              )}
            </div>
          )
        )}

        {picking && !creating && (
          <div className="flex flex-col gap-2">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t('claimContractor.searchPlaceholder', 'Search {{role}}s...', {
                role: roleName.toLowerCase(),
              })}
              iconSize={12}
              className="h-9 text-[13px]"
            />
            {search.length >= 2 && matches.length === 0 && (
              <p className="px-2 py-2 text-[11px] text-[var(--ink-3)]">
                {t('claimContractor.noMatches', 'No {{role}}s found.', {
                  role: roleName.toLowerCase(),
                })}
              </p>
            )}
            {search.length >= 2 && matches.length > 0 && (
              <div className="flex max-h-[180px] flex-col gap-0.5 overflow-y-auto border-t-[0.5px] border-t-[var(--rule)] pt-2">
                {matches.map((p) => (
                  <Button
                    key={p.uuid}
                    variant="ghost"
                    disabled={busy}
                    onClick={() => assign(p.uuid)}
                    className="flex h-auto w-full items-start justify-start gap-2.5 rounded-[4px] px-2 py-2 text-left font-light"
                  >
                    <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-[12px] text-[var(--ink)]">
                        {p.downstream_organisation.name}
                        {(p.child_partnership_count ?? 0) > 0 && (
                          <span className="ml-1.5 text-[10px] uppercase tracking-[0.06em] text-[var(--ink-3)]">
                            {t('claimContractor.headOffice', 'HQ')}
                          </span>
                        )}
                      </span>
                      {/* Branches of one firm share its name, so the name alone
                          cannot tell them apart - OC has three "Mama Mia
                          Roofing" rows differing only by location. The row
                          already carries `attribute_summary` for exactly this;
                          the platform's other partner picker renders it the
                          same way. Tenant-agnostic: whatever attributes a
                          tenant captures are what disambiguates. */}
                      {p.attribute_summary &&
                        (() => {
                          const detail = Object.values(p.attribute_summary)
                            .filter(
                              (v): v is string =>
                                typeof v === 'string' && v.trim().length > 0,
                            )
                            .join(' · ')
                          return detail ? (
                            <span className="truncate text-[11px] text-[var(--ink-3)]">
                              {detail}
                            </span>
                          ) : null
                        })()}
                    </span>
                  </Button>
                ))}
              </div>
            )}
            {/* Creation always starts from the search: a typed name that
                matches no existing partner opens the capture modal, prefilled.
                There is no separate "create" entry point, so the agent always
                looks the partner up first (WPS-163). */}
            {search.trim().length >= 2 &&
              !matches.some(
                (p) =>
                  p.downstream_organisation.name.trim().toLowerCase() ===
                  search.trim().toLowerCase(),
              ) && (
                <Button
                  variant="ghost"
                  onClick={() => setCreating(true)}
                  className="flex h-auto w-full items-center justify-start gap-2.5 rounded-[4px] px-2 py-2 text-left font-light"
                >
                  <Plus className="h-3.5 w-3.5 shrink-0 text-[var(--green)]" />
                  <span className="truncate text-[12px] text-[var(--ink)]">
                    {t('common.addWithName', 'Add "{{name}}"', {
                      name: search.trim(),
                    })}
                  </span>
                </Button>
              )}
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  setPicking(false)
                  setSearch('')
                }}
                className="h-auto px-0 text-[11px] text-[var(--ink-3)]"
              >
                {t('common.cancel', 'Cancel')}
              </Button>
            </div>
          </div>
        )}

        <PartnerCreateDialog
          open={creating || !!editing}
          roleUuid={partnerRole?.uuid}
          roleName={roleName}
          initialCompanyName={creating ? search.trim() : ''}
          editing={editing}
          onCancel={() => {
            setCreating(false)
            setEditing(null)
          }}
          onCreated={(partnership) => {
            // Editing keeps the existing assignment; creating assigns the
            // partner that was just captured.
            if (editing) {
              setEditing(null)
              return
            }
            assign(partnership.uuid)
          }}
          onSelectExisting={(candidate) => assign(candidate.partnership_uuid)}
          // Back to the picker with the typed name intact - the search results
          // are still behind this dialog, so an agent who realises the firm is
          // already on file (often as a branch) does not have to start over.
          onSearchExisting={() => {
            setCreating(false)
            setPicking(true)
          }}
        />
      </div>
    </DetailCard>
  )
}
